import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { login as apiLogin } from "./lib/api";
import { clearSession, getUser, isAuthenticated, setSession } from "./lib/auth";
import { Sidebar } from "./components/layout/Sidebar";
import { MainPane } from "./components/layout/MainPane";
import { RightPane } from "./components/layout/RightPane";
import { Header } from "./components/layout/Header";
import { Login } from "./components/auth/Login";
import { useUI } from "./lib/store";
import { cn } from "./lib/cn";
import { IconButton } from "./components/shared/IconButton";
import { Toasts } from "./components/shared/Toasts";
import { ChevronLeft, ChevronRight, X } from "./components/shared/Icon";
import {
  RIGHT_PANE_MAX,
  RIGHT_PANE_MIN,
  SIDEBAR_MAX,
  SIDEBAR_MIN,
  SIDEBAR_RAIL_WIDTH,
} from "./lib/layout";
import { useResizablePane } from "./hooks/useResizablePane";

export default function App() {
  const [authed, setAuthed] = useState(isAuthenticated);
  const [user, setUser] = useState<string | null>(getUser());
  const layout = useUI((s) => s.layout);
  const setSidebarWidth = useUI((s) => s.setSidebarWidth);
  const setRightPaneWidth = useUI((s) => s.setRightPaneWidth);
  const toggleSidebar = useUI((s) => s.toggleSidebar);
  const toggleRightPaneVisible = useUI((s) => s.toggleRightPaneVisible);
  const rightPaneOpenMobile = useUI((s) => s.rightPaneOpenMobile);
  const setRightPaneOpenMobile = useUI((s) => s.setRightPaneOpenMobile);

  const sidebarHandle = useResizablePane({
    edge: "right",
    min: SIDEBAR_MIN,
    max: SIDEBAR_MAX,
    initial: layout.sidebarWidth,
    collapseBelow: SIDEBAR_MIN - 20,
    onCommit: setSidebarWidth,
    onCollapse: () => {
      if (!layout.sidebarCollapsed) toggleSidebar();
    },
  });

  const rightHandle = useResizablePane({
    edge: "left",
    min: RIGHT_PANE_MIN,
    max: RIGHT_PANE_MAX,
    initial: layout.rightPaneWidth,
    collapseBelow: RIGHT_PANE_MIN - 30,
    onCommit: setRightPaneWidth,
    onCollapse: () => {
      if (layout.rightPaneVisible) toggleRightPaneVisible();
    },
  });

  useEffect(() => {
    const onStorage = () => {
      setAuthed(isAuthenticated());
      setUser(getUser());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!authed) {
    return (
      <Login
        onSubmit={async (u, p) => {
          const { token, username } = await apiLogin(u, p);
          setSession(token, username);
          setUser(username);
          setAuthed(true);
        }}
      />
    );
  }

  const handleLogout = () => {
    clearSession();
    setUser(null);
    setAuthed(false);
  };

  const sidebarWidthPx = layout.sidebarCollapsed
    ? SIDEBAR_RAIL_WIDTH
    : sidebarHandle.dragging
      ? sidebarHandle.width
      : layout.sidebarWidth;

  const rightWidthPx = rightHandle.dragging
    ? rightHandle.width
    : layout.rightPaneWidth;

  return (
    <>
      <div
        className={cn(
          "h-screen w-screen overflow-hidden flex flex-col bg-bg-canvas text-text-primary",
          (sidebarHandle.dragging || rightHandle.dragging) &&
            "cursor-col-resize select-none",
        )}
      >
        <Header username={user ?? undefined} onLogout={handleLogout} />

        <div className="flex-1 flex overflow-hidden relative">
          <aside
            className={cn(
              "hidden md:flex flex-col bg-bg-panel border-r border-border-subtle relative",
              !sidebarHandle.dragging && "transition-[width] duration-base ease-out",
            )}
            style={{ width: sidebarWidthPx }}
          >
            <Sidebar collapsed={layout.sidebarCollapsed} />
            {!layout.sidebarCollapsed && (
              <ResizeHandle {...sidebarHandle.handleProps} side="right" />
            )}
          </aside>

          <main className="flex-1 min-w-0 overflow-hidden flex flex-col bg-bg-canvas relative">
            <MainPane />
            {!layout.rightPaneVisible && (
              <button
                onClick={toggleRightPaneVisible}
                aria-label="Show right pane"
                className={cn(
                  "hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2",
                  "h-16 w-5 items-center justify-center",
                  "bg-bg-panel border border-r-0 border-border-subtle rounded-l-md",
                  "text-text-tertiary hover:text-text-primary hover:bg-bg-inset",
                  "transition-colors duration-fast ease-out",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1",
                )}
              >
                <ChevronLeft />
              </button>
            )}
          </main>

          {layout.rightPaneVisible && (
            <aside
              className={cn(
                "hidden lg:flex flex-col bg-bg-panel border-l border-border-subtle relative",
                !rightHandle.dragging && "transition-[width] duration-base ease-out",
              )}
              style={{ width: rightWidthPx }}
            >
              <ResizeHandle {...rightHandle.handleProps} side="left" />
              <RightPane />
              <button
                onClick={toggleRightPaneVisible}
                aria-label="Hide right pane"
                className={cn(
                  "absolute top-2 right-2 z-10 h-6 w-6 inline-flex items-center justify-center rounded-sm",
                  "text-text-tertiary hover:text-text-primary hover:bg-bg-inset",
                  "transition-colors duration-fast ease-out",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1",
                )}
              >
                <ChevronRight />
              </button>
            </aside>
          )}

          <AnimatePresence>
            {rightPaneOpenMobile && (
              <motion.div
                key="drawer"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                className={cn(
                  "fixed inset-x-0 bottom-0 top-12 z-40",
                  "bg-bg-panel border-t border-border-subtle lg:hidden",
                  "flex flex-col shadow-lg",
                )}
              >
                <div className="h-11 flex items-center justify-between px-4 border-b border-border-subtle">
                  <span className="text-caption text-text-tertiary">Detail</span>
                  <IconButton
                    icon={<X />}
                    label="Close drawer"
                    onClick={() => setRightPaneOpenMobile(false)}
                    size="sm"
                  />
                </div>
                <div className="flex-1 overflow-hidden flex flex-col">
                  <RightPane />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <Toasts />
    </>
  );
}

function ResizeHandle({
  side,
  ...rest
}: { side: "left" | "right" } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      aria-label={`Resize ${side === "right" ? "sidebar" : "right pane"}`}
      className={cn(
        "absolute top-0 bottom-0 w-1.5 z-20 cursor-col-resize",
        "hover:bg-accent-soft active:bg-accent-soft",
        "transition-colors duration-fast ease-out",
        side === "right" ? "-right-[3px]" : "-left-[3px]",
      )}
    />
  );
}
